import ChangeOrderDetailHeader from "./detail/ChangeOrderDetailHeader";
import { useParams, useSearchParams } from "react-router-dom";
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { MdRateReview } from "react-icons/md";
import { FaCentercode, FaExclamationTriangle, FaHistory, FaRetweet, FaWpforms } from "react-icons/fa";
import { LoadingSpinner, Tabs, Button, DetailRecordFallback } from 'components/common';
import { getChangeOrder } from "redux/crm/project/changeOrderSlice";
import { useDispatch } from "react-redux";
import { useDetailRecord } from "helpers/detailRequest";

const LazyChangeOrderDetailForm = lazy(() => import("./detail/ChangeOrderDetailForm"));
const LazyChangeOrderDetailInputReview = lazy(() => import("./detail/ChangeOrderDetailInputReview"));
const LazyChangeOrderDetailEnterJob = lazy(() => import("./detail/ChangeOrderDetailEnterJob"));
const LazyChangeOrderActivity = lazy(() => import("./detail/ChangeOrderActivity"));
// webpackPrefetch: the Revisions tab pulls in ag-grid-enterprise (a heavy chunk).
// Prefetching lets the browser download it during idle time after the detail
// renders, so opening the tab doesn't wait on the JS. The tab fetches its own
// data (once per CO#) — no data prewarm/seed here, which raced with the tab's
// own fetch and left the grid stuck on "Loading" when re-opened.
const LazyRevisionsTab = lazy(() => import(/* webpackPrefetch: true */ "../change-order-revision/RevisionsTab"));

const ChangeOrderDetail = () => {
  const params = useParams();
  const dispatch = useDispatch();

  // Never the CO# of another job left in the persisted store — an email link would
  // show the wrong job and its REV# panel would open against it.
  const { record, waiting, loading, error } = useDetailRecord("crm_change_order", params.id, "changeOrderData");
  const changeOrder = record || undefined;

  // Tracked by tab id, not index: conditional tabs are filtered out of the list,
  // so a tab's index shifts once `changeOrder` loads and `isNotCo00` settles.
  // Revision mail links deep-link here as ?revision=<id> — land on that tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedRevision = searchParams.get("revision");
  const [activeId, setActiveId] = useState(deepLinkedRevision ? "co-revisions" : "co-enterJob");

  const pendingRevisions = changeOrder?.pending_revisions;

  // Same page, new link (another REV# email while this page is open).
  useEffect(() => {
    if (deepLinkedRevision) setActiveId("co-revisions");
  }, [deepLinkedRevision]);

  // Jump to the Revisions tab and open the given REV# for accept/reject.
  const reviewRevision = (id) => {
    setActiveId("co-revisions");
    const next = new URLSearchParams(searchParams);
    next.set("revision", id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    dispatch(getChangeOrder({
      projectId: params.project_id,
      buildingId: params.building_id,
      changeOrderId: params.id}
    ))
  }, [params.project_id, params.building_id, params.id, dispatch]);

  const isNotCo00 = changeOrder?.co !== "00";
  // Revisions only exist once CSD has accepted the CO#.
  const showRevisions = changeOrder?.co_status === "accepted";

  const revisionLatest = changeOrder?.revision_latest;
  const showRevisionBadge = revisionLatest && revisionLatest !== "00";

  const tabs = useMemo(() => [
    isNotCo00 && {
      id: "co-form",
      label: "Change Order Form",
      icon: <FaWpforms />,
      content: <LazyChangeOrderDetailForm changeOrder={changeOrder} />
    },
    {
      id: "co-enterJob",
      label: "Enter job",
      icon: <FaCentercode />,
      content: <LazyChangeOrderDetailEnterJob changeOrder={changeOrder} />
    },
    {
      id: "co-inputReview",
      label: "Input review",
      icon: <MdRateReview />,
      content: <LazyChangeOrderDetailInputReview changeOrder={changeOrder} />
    },
    showRevisions && {
      id: "co-revisions",
      label: "Revisions",
      icon: <FaRetweet />,
      badge: showRevisionBadge ? parseInt(revisionLatest) : undefined,
      badgeColor: "#f5803e",
      content: <LazyRevisionsTab changeOrder={changeOrder} />
    },
    {
      id: "co-activity-log",
      label: "Activities",
      icon: <FaHistory />,
      content: <LazyChangeOrderActivity />
    }
  ].filter(Boolean), [isNotCo00, showRevisions, showRevisionBadge, revisionLatest, changeOrder]);

  // Falls back to the first tab if the active one disappears (e.g. the
  // Revisions tab after the CO leaves the accepted state).
  const activeTab = Math.max(tabs.findIndex(tab => tab.id === activeId), 0);

  if (!record) {
    return (
      <DetailRecordFallback
        waiting={waiting}
        title="Change order not available"
        error={error}
        onRetry={() => dispatch(getChangeOrder({ projectId: params.project_id, buildingId: params.building_id, changeOrderId: params.id }))}
      />
    );
  }

  return (
    <>
      <ChangeOrderDetailHeader changeOrder={changeOrder}/>

      {loading &&
        <LoadingSpinner size="md" className="!tw-py-2" />
      }

      {pendingRevisions?.count > 0 && (
        <div className="tw-mx-3 tw-mb-2 tw-rounded-md tw-border-l-4 tw-border-solid tw-border-y-0 tw-border-r-0 tw-border-amber-500 tw-bg-amber-50 tw-px-4 tw-py-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
            <FaExclamationTriangle className="tw-text-amber-500 tw-flex-shrink-0" />
            <span className="tw-text-sm tw-text-amber-900">
              <strong>{pendingRevisions.count} revision{pendingRevisions.count === 1 ? '' : 's'} waiting for your accept / reject</strong>
              <span className="tw-text-amber-800"> — {pendingRevisions.revs.map(r => `REV#${r.rev}`).join(', ')}</span>
            </span>
          </div>
          <Button variant="primary" size="sm" onClick={() => reviewRevision(pendingRevisions.revs[0].id)}>
            Review REV#{pendingRevisions.revs[0].rev}
          </Button>
        </div>
      )}

      <div className="tw-bg-white tw-overflow-hidden">
        <Suspense fallback={<LoadingSpinner size="md" className="!tw-py-8" />}>
          {/* lazyMount: only the viewed tab mounts (then stays mounted). Avoids
              every tab fetching/rendering on open — Enter job, Revisions (AG Grid
              + fetch), Activities (audit log) mount only when first opened. */}
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={(index) => setActiveId(tabs[index].id)}
            variant="underline"
            lazyMount
            navClassName="tw-px-2 tw-overflow-x-auto tw-overflow-y-hidden tw-flex-nowrap !tw-border-b-0"
            contentClassName="tw-p-3"
          />
        </Suspense>
      </div>
    </>
  )
};

export default ChangeOrderDetail;
